import { useEffect, useState } from "react";
import { Box, Typography, Table, TableRow, TableBody, TableHead, TableContainer, Pagination, Chip, IconButton, Dialog, DialogTitle, DialogContent, Tabs, Tab, Tooltip } from "@mui/material";
import Button from "components/atoms/Button";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { MODAL, modalActions } from "redux/slices/modal";
import AssetManagementService from "services/assetManagement.service";
import { assetManagementActions } from "redux/slices/assetManagement/assetManagementSlice";
import TableLoadingWrapper from "components/templates/TableLoadingWrapper";
import { StyledTableCell, StyledTableRow } from "components/templates/Tables";
import { useNavigate } from "react-router-dom";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DateService from "utils/date.util";
import AssetFilters from "./AssetFilters";

export default function AssetListing() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const loading = useAppSelector((s) => s.assetManagement.loading);
    const assetListingData = useAppSelector((s) => s.assetManagement.assetListingData) as any[];
    const count = useAppSelector((s) => s.assetManagement.count) || 0;
    const filters = useAppSelector((s) => s.assetManagement.filters);
    const refresh = useAppSelector((s) => s.assetManagement.refresh) || 0;
    const refreshLoader = useAppSelector((s) => s.assetManagement.refreshLoader) || false;
    
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<any>(null);
    const [imageTab, setImageTab] = useState(0);

    useEffect(() => {
        return () => {
            dispatch(assetManagementActions.resetPage());
        };
    }, [dispatch]);

    useEffect(() => {
        AssetManagementService.getAssetListing(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, refresh]);

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">Asset Listing</Typography>
                <Button
                    variant="contained"
                    onClick={() => {
                        navigate("/add-asset");
                    }}
                >
                    Add Asset
                </Button>
            </Box>

            <AssetFilters />

            <div className="pagination-list">
                <p>{count > 0 && `${count} Asset${count > 1 ? "s" : ""}`}</p>
                {count > 0 && (
                    <Pagination
                        page={filters.page}
                        disabled={refreshLoader}
                        count={Math.ceil(count / filters.page_size)}
                        onChange={(_e, page) => dispatch(assetManagementActions.setPage(page))}
                    />
                )}
            </div>

            <TableContainer>
                <Table
                    aria-label="asset listing table"
                    sx={{
                        minWidth: "100%",
                        borderSpacing: "0 10px",
                        borderBottomWidth: "0px",
                        borderCollapse: "separate",
                    }}
                >
                    <TableLoadingWrapper
                        coloumns={7}
                        loading={loading}
                        length={refreshLoader ? 0 : assetListingData.length}
                        message="No assets found"
                    >
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell>Items</StyledTableCell>
                                <StyledTableCell>Items Number</StyledTableCell>
                                <StyledTableCell>Driver</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="center">Images</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {assetListingData.map((asset: any, index: number) => {
                                const driverName = asset.driver_id 
                                    ? `${asset.driver_id.first_name || ""} ${asset.driver_id.last_name || ""}`.trim()
                                    : "-";
                                const startDate = asset.start_date 
                                    ? DateService.getDateString(asset.start_date, "DD.MM.YYYY")
                                    : "-";
                                const startTime = asset.start_date 
                                    ? DateService.getTimeString(asset.start_date)
                                    : "";
                                const endDate = asset.end_date 
                                    ? DateService.getDateString(asset.end_date, "DD.MM.YYYY")
                                    : null;
                                const endTime = asset.end_date 
                                    ? DateService.getTimeString(asset.end_date)
                                    : "";
                                
                                // Handle category_id as array or single
                                const categories = Array.isArray(asset.category_id) 
                                    ? asset.category_id.map((cat: any) => cat?.name || cat).join(", ")
                                    : asset.category_id?.name || "-";
                                
                                // Handle subcategory_id as array or single
                                const subcategories = Array.isArray(asset.subcategory_id)
                                    ? asset.subcategory_id.map((sub: any) => sub?.name || sub).join(", ")
                                    : asset.subcategory_id?.name || "-";
                                
                                return (
                                    <StyledTableRow key={asset._id || index}>
                                        <StyledTableCell>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                <Typography variant="body2">
                                                    <strong>Taken:</strong>
                                                    <br />
                                                    {startDate}
                                                    {startTime && (
                                                        <>
                                                            <br />
                                                            {startTime}
                                                        </>
                                                    )}
                                                </Typography>
                                                {endDate && (
                                                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                                                        <strong>Return:</strong>
                                                        <br />
                                                        {endDate}
                                                        {endTime && (
                                                            <>
                                                                <br />
                                                                {endTime}
                                                            </>
                                                        )}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell>{categories}</StyledTableCell>
                                        <StyledTableCell>{subcategories}</StyledTableCell>
                                        <StyledTableCell>{driverName}</StyledTableCell>
                                        <StyledTableCell>
                                            <Chip
                                                size="small"
                                                label={asset.status === "active" ? "Active" : "Returned"}
                                                sx={{
                                                    backgroundColor: asset.status === "active" 
                                                        ? "rgba(76, 175, 80, 0.12)" 
                                                        : "rgba(33, 150, 243, 0.12)",
                                                    color: asset.status === "active" 
                                                        ? "rgba(76, 175, 80, 0.9)" 
                                                        : "rgba(33, 150, 243, 0.9)",
                                                    border: asset.status === "active" 
                                                        ? "1px solid rgba(76, 175, 80, 0.3)" 
                                                        : "1px solid rgba(33, 150, 243, 0.3)",
                                                }}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                                {asset.images?.taken && asset.images.taken.length > 0 && (
                                                    <Tooltip title={`${asset.images.taken.length} Taken Image${asset.images.taken.length > 1 ? "s" : ""}`}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => {
                                                                setSelectedAsset(asset);
                                                                setImageTab(0);
                                                                setImageDialogOpen(true);
                                                            }}
                                                        >
                                                            <ImageIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {asset.images?.returned && asset.images.returned.length > 0 && (
                                                    <Tooltip title={`${asset.images.returned.length} Returned Image${asset.images.returned.length > 1 ? "s" : ""}`}>
                                                        <IconButton
                                                            size="small"
                                                            color="secondary"
                                                            onClick={() => {
                                                                setSelectedAsset(asset);
                                                                setImageTab(1);
                                                                setImageDialogOpen(true);
                                                            }}
                                                        >
                                                            <ImageIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {(!asset.images?.taken || asset.images.taken.length === 0) && 
                                                 (!asset.images?.returned || asset.images.returned.length === 0) && (
                                                    <span style={{ color: "#999", fontSize: "12px" }}>-</span>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => {
                                                        navigate(`/update-asset/${asset._id}`);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                {!asset.end_date && asset.status === "active" && (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => {
                                                            dispatch(
                                                                modalActions.openModal({
                                                                    type: MODAL.RETURN_ASSET,
                                                                    data: { asset },
                                                                    width: "600px",
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        Return
                                                    </Button>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })}
                        </TableBody>
                    </TableLoadingWrapper>
                </Table>
            </TableContainer>

            {/* Image Gallery Dialog */}
            <Dialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="h6">Asset Images</Typography>
                        <IconButton onClick={() => setImageDialogOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedAsset && (
                        <>
                            {(selectedAsset.images?.taken?.length > 0 || selectedAsset.images?.returned?.length > 0) && (
                                <Tabs value={imageTab} onChange={(_, newValue) => setImageTab(newValue)} sx={{ mb: 2 }}>
                                    {selectedAsset.images?.taken?.length > 0 && (
                                        <Tab label={`Taken (${selectedAsset.images.taken.length})`} />
                                    )}
                                    {selectedAsset.images?.returned?.length > 0 && (
                                        <Tab label={`Returned (${selectedAsset.images.returned.length})`} />
                                    )}
                                </Tabs>
                            )}
                            
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center" }}>
                                {imageTab === 0 && selectedAsset.images?.taken?.length > 0 && (
                                    selectedAsset.images.taken.map((imageUrl: string, index: number) => (
                                        <Box
                                            key={index}
                                            onClick={() => window.open(imageUrl, "_blank")}
                                            sx={{
                                                width: "150px",
                                                height: "150px",
                                                border: "1px solid #ddd",
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                overflow: "hidden",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: "#f5f5f5",
                                                "&:hover": {
                                                    opacity: 0.8,
                                                    borderColor: "primary.main",
                                                },
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={imageUrl}
                                                alt={`Taken image ${index + 1}`}
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </Box>
                                    ))
                                )}
                                
                                {imageTab === 1 && selectedAsset.images?.returned?.length > 0 && (
                                    selectedAsset.images.returned.map((imageUrl: string, index: number) => (
                                        <Box
                                            key={index}
                                            onClick={() => window.open(imageUrl, "_blank")}
                                            sx={{
                                                width: "150px",
                                                height: "150px",
                                                border: "1px solid #ddd",
                                                borderRadius: 1,
                                                cursor: "pointer",
                                                overflow: "hidden",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: "#f5f5f5",
                                                "&:hover": {
                                                    opacity: 0.8,
                                                    borderColor: "primary.main",
                                                },
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={imageUrl}
                                                alt={`Returned image ${index + 1}`}
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        </Box>
                                    ))
                                )}
                                
                                {imageTab === 0 && (!selectedAsset.images?.taken || selectedAsset.images.taken.length === 0) && (
                                    <Typography color="textSecondary">No taken images</Typography>
                                )}
                                
                                {imageTab === 1 && (!selectedAsset.images?.returned || selectedAsset.images.returned.length === 0) && (
                                    <Typography color="textSecondary">No returned images</Typography>
                                )}
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
